import React, { useState, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

import Icon from '@components/icon';
import Link from '@components/link';
import ContentModal from '@components/content-modal';
import { pushDataLayerEvent } from '@lib/gtm';

// ---------------------------------------------------------

import { ModalContentType } from '../../types/modal';
import styles from './styles.module.scss';

const {
  button,
  icon_container,
  theme_black,
  theme_gray_outline,
  theme_red_outline,
  theme_red,
  theme_text_only_button,
  theme_text_only_red_button,
  theme_text_only_white_button,
  theme_text_only_black_button,
  theme_text_only_white_button_with_caret_button,
  theme_with_icon,
  theme_red_text_only_with_caret_button,
  theme_text_only_with_caret_button,
  container
} = styles;

const themeOptions = {
  black: theme_black,
  'gray-outline': theme_gray_outline,
  red: theme_red,
  'red-outline': theme_red_outline,
  'red-text-only-with-caret': theme_red_text_only_with_caret_button,
  'text-only': theme_text_only_button,
  'text-only-white': theme_text_only_white_button,
  'text-only-black': theme_text_only_black_button,
  'text-only-white-with-caret': theme_text_only_white_button_with_caret_button,
  'text-only-red': theme_text_only_red_button,
  'text-only-with-caret': theme_text_only_with_caret_button,
  with_icon: theme_with_icon
};

// ---------------------------------------------------------

export interface ButtonProps {
  className?: string;
  dataLayerEvent?: string;
  disabled?: boolean;
  icon?: string;
  iconType?: string;
  label?: string;
  modalButton?: boolean;
  modal?: ModalContentType;
  onClick?: (e: React.MouseEvent) => void;
  theme?: keyof typeof themeOptions;
  title?: string;
  type?: 'submit' | 'button';
  url?: string;
  query?: Record<string, unknown>;
  pageUrl?: string;
  contextData?: Record<string, unknown>;
}

// ---------------------------------------------------------

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (props, ref) => {
    const {
      className,
      dataLayerEvent,
      disabled,
      icon,
      iconType,
      label,
      modalButton,
      modal,
      onClick,
      theme = 'red',
      title,
      type = 'submit',
      url,
      query,
      pageUrl,
      contextData
    } = props;

    const router = useRouter();
    const [modalOpen, setModalOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const currentPageUrl = pageUrl || router.asPath;

    // -------------------------------------------------------

    const anchorUrl = url?.match(/^#/);

    const handleScrollTo = () => {
      if (!url) return;
      if (dataLayerEvent) {
        pushDataLayerEvent(dataLayerEvent, url);
      }
      const element = document.getElementById(url.slice(1));
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    };

    const handleLinkClick = () => {
      if (dataLayerEvent) {
        pushDataLayerEvent(dataLayerEvent, url);
      }
    };

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (dataLayerEvent) {
          pushDataLayerEvent(dataLayerEvent, router.asPath);
        }

        if (modal) {
          e.preventDefault();
          setModalOpen(true);
          return;
        }

        if (anchorUrl) {
          e.preventDefault();
          handleScrollTo();
          return;
        }

        if (onClick) {
          onClick(e);
        }
      },
      [modal, anchorUrl, onClick, url, dataLayerEvent]
    );

    const handleModalClose = useCallback(() => {
      setModalOpen(false);
    }, []);

    // -------------------------------------------------------

    const classes = classNames({
      [button]: theme !== 'text-only',
      ...(className && { [className]: true }),
      [themeOptions[theme]]: themeOptions[theme]
    });

    // -------------------------------------------------------

    const showCarets =
      theme === 'text-only-white-with-caret' ||
      theme === 'text-only-with-caret' ||
      theme === 'red-text-only-with-caret';

    const buttonContents = (
      <div className={container}>
        {icon && (
          <span className={icon_container}>
            <Icon type={iconType ? iconType : `filters`} name={icon} />
          </span>
        )}
        {label}
        <div>{showCarets && <Icon type={'arrow'} name={'small-right'} />}</div>
      </div>
    );

    const modalContextData = {
      ctaTheme: theme,
      ctaLabel: label,
      pageUrl: currentPageUrl,
      ...contextData
    };

    const shouldRenderAsButton = !!(
      modal ||
      modalButton ||
      (!url && (onClick || type)) ||
      anchorUrl
    );

    let buttonComponent: React.ReactElement;

    if (shouldRenderAsButton) {
      buttonComponent = (
        <button
          ref={buttonRef}
          className={classes}
          onClick={handleClick}
          disabled={disabled}
          type={anchorUrl || modal ? 'button' : type}
          aria-haspopup={modal ? 'dialog' : undefined}
          aria-expanded={modal ? modalOpen : undefined}
        >
          {buttonContents}
        </button>
      );
    } else {
      buttonComponent = (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classes}
          url={url}
          query={query}
          title={title}
          onClick={dataLayerEvent ? handleLinkClick : undefined}
        >
          {buttonContents}
        </Link>
      );
    }

    if (modalButton && !modal) {
      return <div className={classes}>{buttonContents}</div>;
    }

    if (modal) {
      return (
        <>
          {buttonComponent}
          <ContentModal
            modalData={modal}
            isOpen={modalOpen}
            onClose={handleModalClose}
            triggerElement={buttonRef.current}
            pageUrl={currentPageUrl}
            contextData={modalContextData}
          />
        </>
      );
    }

    return buttonComponent;
  }
);

Button.displayName = 'Button';

export default Button;
